import { lazy, Suspense, useState } from 'react'
import {
  AlertTriangle, CheckCircle, Download, ExternalLink, Loader2, RefreshCw, Tag, Trash2, X,
} from 'lucide-react'
import { Modal, InlineField, EmployeeInvoiceField } from '@/components/common'
import { documents as docsApi } from '@/utils/api'

// react-pdf nur bei Bedarf nachladen — geteilt mit der Buchhaltung
const FilePreviewPane = lazy(() => import('@/components/common/FilePreviewPane'))

export interface DocumentDetailHandlers {
  onSave: (field: string, value: string | number | null) => Promise<unknown>
  onToggleDone: (done: boolean) => void
  onSaveTags: (tags: string[]) => void
  onTransfer: () => void
  onSetBookingState: (state: 'pending' | 'ignored') => void
  onOpenInvoice: (invoiceId: number) => void
  onReanalyze: () => void
  onDelete: () => void
}

interface Props {
  doc: any | null
  loading: boolean
  categories: Record<string, { label: string; color: string }>
  paymentMethods: string[]
  statusConfig: Record<string, { label: string; color: string }>
  transferPending: boolean
  onClose: () => void
  handlers: DocumentDetailHandlers
}

const formatDate = (s?: string | null) =>
  s ? new Date(s).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' }) : '-'

const formatFileSize = (bytes?: number | null) => {
  if (!bytes) return '-'
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

const parseTags = (raw: unknown): string[] => {
  if (!raw) return []
  if (Array.isArray(raw)) return raw
  try {
    const parsed = JSON.parse(String(raw))
    return Array.isArray(parsed) ? parsed : []
  } catch {
    return String(raw).split(',').map((t) => t.trim()).filter(Boolean)
  }
}

/**
 * Detailansicht eines Dokuments — bewusst identisch aufgebaut zum Rechnungs-Modal
 * der Buchhaltung (Kundenwunsch „einheitlicher Look"): Fullscreen, links auf 60 %
 * die Datei mit horizontaler Seiten-Leiste, rechts auf 40 % die Daten, Kopf- und
 * Fussleiste gepinnt. Vorschau und Feld-Bearbeitung teilen sich dieselben
 * Komponenten (FilePreviewPane, InlineField).
 */
export default function DocumentDetailModal({
  doc, loading, categories, paymentMethods, statusConfig, transferPending, onClose, handlers,
}: Props) {
  const [newTag, setNewTag] = useState('')

  const isOpen = !!doc || loading
  const tags = parseTags(doc?.tags)
  const isPdf = doc?.mime_type === 'application/pdf'
  const isImage = !!doc?.mime_type?.startsWith('image/')
  const canPreview = isPdf || isImage
  const status = statusConfig[doc?.analysis_status] || statusConfig.pending

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="screen"
      className="flex flex-col"
      bodyClassName="flex min-h-0 flex-1 flex-col"
      headerContent={
        <div className="flex flex-shrink-0 flex-wrap items-center gap-x-3 gap-y-1.5 border-b border-gray-200 bg-white px-4 py-2.5">
          {doc ? (
            <>
              <span className={`flex-shrink-0 whitespace-nowrap rounded border border-gray-200 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider ${
                categories[doc.category]?.color || 'text-gray-500'
              }`}>
                {categories[doc.category]?.label || doc.category || 'Ohne Kategorie'}
              </span>

              <span className="min-w-0 flex-1">
                <InlineField
                  label=""
                  field="title"
                  value={doc.title || doc.original_file_name}
                  onSave={handlers.onSave}
                  labelWidth={0}
                  className="text-base font-bold tracking-tight"
                />
              </span>

              <span className={`flex-shrink-0 whitespace-nowrap rounded-full px-2.5 py-0.5 text-[11px] font-medium ${status.color}`}>
                {status.label}
              </span>

              {doc.booking_status === 'transferred' && (
                <button
                  type="button"
                  onClick={() => handlers.onOpenInvoice(doc.invoice_id)}
                  className="flex-shrink-0 whitespace-nowrap rounded-full bg-green-100 px-2.5 py-0.5 text-[11px] font-medium text-green-700 transition hover:brightness-95"
                  title="In der Buchhaltung öffnen"
                >
                  In Buchhaltung
                </button>
              )}
              {doc.booking_status === 'pending' && (
                <span className="flex-shrink-0 whitespace-nowrap rounded-full bg-orange-100 px-2.5 py-0.5 text-[11px] font-medium text-orange-800 ring-1 ring-orange-300">
                  Buchhaltung offen
                </span>
              )}

              <button
                type="button"
                onClick={() => handlers.onToggleDone(!doc.is_done)}
                title={doc.is_done ? 'Erledigt-Markierung entfernen' : 'Als erledigt markieren'}
                className={`flex h-8 flex-shrink-0 items-center gap-1.5 rounded-md border px-2 text-xs font-semibold transition-colors ${
                  doc.is_done
                    ? 'border-green-200 bg-green-100 text-green-700'
                    : 'border-gray-200 text-gray-500 hover:bg-gray-50 hover:text-gray-900'
                }`}
              >
                <CheckCircle className="h-4 w-4" />
                {/* Nicht "Offen" — das stuende widerspruechlich neben dem
                    Analyse-Status "Fertig" direkt daneben. */}
                {doc.is_done ? 'Erledigt' : 'Als erledigt markieren'}
              </button>
            </>
          ) : (
            <span className="flex-1 text-sm text-gray-400">Dokument wird geladen…</span>
          )}

          <button
            type="button"
            onClick={onClose}
            title="Schließen"
            className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-md text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-900"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      }
    >
      {!doc ? (
        <div className="flex flex-1 items-center justify-center">
          <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
        </div>
      ) : (
        <div className="grid min-h-0 flex-1 grid-rows-[minmax(340px,55vh)_auto] overflow-y-auto xl:grid-cols-[60fr_40fr] xl:grid-rows-1 xl:overflow-hidden">
          {/* Datei */}
          <div className="min-h-0 min-w-0 border-b border-gray-200 xl:border-b-0 xl:border-r">
            <Suspense fallback={
              <div className="flex h-full items-center justify-center bg-gray-100">
                <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
              </div>
            }>
              <FilePreviewPane
                url={canPreview ? docsApi.getFileUrl(doc.id) : null}
                fileName={doc.original_file_name}
                isPdf={isPdf}
                emptyHint={'Für diesen Dateityp gibt es keine Vorschau — über „Herunterladen“ öffnen.'}
              />
            </Suspense>
          </div>

          {/* Daten */}
          <div className="relative min-h-0 space-y-2.5 bg-gray-50 p-3 xl:overflow-y-auto">
            {/* Buchhaltungs-Hinweise */}
            {doc.booking_status === 'pending' && (
              <div className="rounded-lg border border-orange-200 bg-orange-50 p-3">
                <p className="flex items-center gap-1.5 text-xs font-semibold text-orange-900">
                  <AlertTriangle className="h-4 w-4 flex-shrink-0" />
                  Noch nicht in der Buchhaltung
                </p>
                <p className="mt-1 text-xs leading-relaxed text-orange-800">
                  Als Rechnung erkannt, aber nicht übernommen. Die erkannten Rechnungsdaten liegen bereit.
                </p>
                <div className="mt-2 flex flex-wrap gap-2">
                  <button
                    type="button"
                    disabled={transferPending}
                    onClick={handlers.onTransfer}
                    className="inline-flex items-center gap-1.5 rounded-md bg-primary-500 px-2.5 py-1 text-xs font-semibold text-white transition-colors hover:bg-primary-600 disabled:opacity-60"
                  >
                    {transferPending
                      ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      : <ExternalLink className="h-3.5 w-3.5" />}
                    In Buchhaltung übernehmen
                  </button>
                  <button
                    type="button"
                    onClick={() => handlers.onSetBookingState('ignored')}
                    className="rounded-md border border-gray-300 bg-white px-2.5 py-1 text-xs font-semibold text-gray-700 transition-colors hover:bg-gray-50"
                  >
                    Nicht für Buchhaltung
                  </button>
                </div>
              </div>
            )}

            {doc.booking_status === 'ignored' && (
              <div className="flex flex-wrap items-center gap-2 rounded-lg border border-gray-200 bg-white px-3 py-2 text-xs text-gray-600">
                Als <span className="font-medium">nicht für die Buchhaltung</span> markiert.
                <button
                  type="button"
                  onClick={() => handlers.onSetBookingState('pending')}
                  className="font-semibold text-primary-600 hover:text-primary-700"
                >
                  Doch übernehmen
                </button>
              </div>
            )}

            {/* Analysefehler */}
            {doc.analysis_status === 'failed' && (
              <div className="rounded-lg border border-amber-200 bg-amber-50 p-3">
                <p className="flex items-center gap-1.5 text-xs font-semibold text-amber-800">
                  <AlertTriangle className="h-4 w-4 flex-shrink-0" />
                  {doc.analysis_error?.includes('überlastet') || doc.analysis_error?.includes('stack')
                    ? 'Dokument konnte noch nicht verarbeitet werden'
                    : 'Analyse fehlgeschlagen'}
                </p>
                <p className="mt-1 text-xs leading-relaxed text-amber-700">
                  {doc.analysis_error?.includes('überlastet') || doc.analysis_error?.includes('stack')
                    ? 'Der KI-Dienst ist gerade stark ausgelastet. Das Dokument ist sicher gespeichert — in ein paar Minuten erneut versuchen.'
                    : `Problem bei der Analyse: ${doc.analysis_error || 'Unbekannter Fehler'}.`}
                </p>
                <button
                  type="button"
                  onClick={handlers.onReanalyze}
                  className="mt-2 inline-flex items-center gap-1.5 rounded-md bg-amber-100 px-2.5 py-1 text-xs font-semibold text-amber-800 transition-colors hover:bg-amber-200"
                >
                  <RefreshCw className="h-3.5 w-3.5" /> Erneut versuchen
                </button>
              </div>
            )}

            {/* Einordnung */}
            <section className="rounded-lg border border-gray-200 bg-white">
              <InlineField
                label="Kategorie" field="category" value={doc.category}
                type="select"
                options={Object.keys(categories)}
                optionLabel={(k) => categories[k]?.label || k}
                onSave={handlers.onSave}
              />
              <InlineField
                label="Zahlungsart" field="payment_method" value={doc.payment_method}
                type="select" options={paymentMethods} onSave={handlers.onSave}
              />

              {/* Schlagworte */}
              <div className="grid min-h-[30px] grid-cols-[104px_minmax(0,1fr)] items-start gap-2 border-t border-gray-100 px-3 py-1.5">
                <label className="flex items-center gap-1 pt-1 text-[11px] text-gray-400">
                  <Tag className="h-3 w-3" /> Schlagworte
                </label>
                <div className="flex flex-wrap items-center gap-1.5">
                  {tags.map((tag, i) => (
                    <span key={`${tag}-${i}`} className="inline-flex items-center gap-1 rounded bg-gray-100 px-2 py-0.5 text-xs text-gray-700">
                      {tag}
                      <button
                        type="button"
                        aria-label={`Schlagwort ${tag} entfernen`}
                        onClick={() => handlers.onSaveTags(tags.filter((_, idx) => idx !== i))}
                        className="text-gray-400 transition-colors hover:text-red-500"
                      >×</button>
                    </span>
                  ))}
                  <form
                    className="inline-flex"
                    onSubmit={(e) => {
                      e.preventDefault()
                      const t = newTag.trim()
                      if (!t || tags.includes(t)) { setNewTag(''); return }
                      handlers.onSaveTags([...tags, t])
                      setNewTag('')
                    }}
                  >
                    <input
                      value={newTag}
                      onChange={(e) => setNewTag(e.target.value)}
                      placeholder="+"
                      aria-label="Schlagwort hinzufügen"
                      className="w-16 rounded border border-dashed border-gray-300 px-1.5 py-0.5 text-xs transition-all focus:w-32 focus:border-primary-400 focus:outline-none"
                    />
                  </form>
                </div>
              </div>

              {/* #124 Mitarbeiter-Rechnung. Bis 14.08.2026 haing der Haken an
                  `doc._is_employee` — ein Feld, das die API nie geliefert hat, und
                  er erschien ueberhaupt nur bei bereits uebernommenen Dokumenten.
                  Beides ist weg: die Markierung gehoert an den Beleg, wenn man ihn
                  sichtet, also lange vor der Buchhaltung. */}
              {['eingangsrechnung', 'ausgangsrechnung'].includes(doc.category) && (
                <EmployeeInvoiceField
                  checked={doc.is_employee_invoice}
                  employeeName={doc.employee_name}
                  onSave={handlers.onSave}
                />
              )}
            </section>

            {/* Zusammenfassung */}
            {doc.summary && (
              <section className="rounded-lg border border-gray-200 bg-white p-3">
                <p className="mb-1 text-[10px] font-bold uppercase tracking-wider text-gray-400">Zusammenfassung</p>
                <p className="text-sm text-gray-800">{doc.summary}</p>
              </section>
            )}

            {/* Metadaten */}
            <section className="rounded-lg border border-gray-200 bg-white">
              <Meta label="Hochgeladen" value={formatDate(doc.created_at)} />
              {doc.uploaded_by_name && <Meta label="Von" value={doc.uploaded_by_name} />}
              <Meta label="Datei" value={doc.original_file_name} mono />
              <Meta label="Seiten" value={doc.page_count || '–'} />
              <Meta label="Größe" value={formatFileSize(doc.file_size)} />
            </section>
          </div>
        </div>
      )}

      {/* Gepinnte Aktionsleiste */}
      {doc && (
        <div className="flex flex-shrink-0 flex-wrap items-center gap-2 border-t border-gray-200 bg-gray-50 px-4 py-2.5">
          <button
            type="button"
            onClick={handlers.onDelete}
            className="inline-flex items-center gap-1.5 rounded-md border border-red-200 bg-white px-3 py-1.5 text-xs font-semibold text-red-600 transition-colors hover:border-red-500 hover:bg-red-50"
          >
            <Trash2 className="h-3.5 w-3.5" /> Löschen
          </button>

          <span className="flex-1" />

          <a
            href={docsApi.getFileUrl(doc.id)}
            download={doc.original_file_name}
            className="inline-flex items-center gap-1.5 rounded-md border border-gray-200 bg-white px-3 py-1.5 text-xs font-semibold text-gray-600 transition-colors hover:border-gray-400 hover:text-gray-900"
          >
            <Download className="h-3.5 w-3.5" /> Herunterladen
          </a>

          {doc.invoice_id ? (
            <button
              type="button"
              onClick={() => handlers.onOpenInvoice(doc.invoice_id)}
              className="inline-flex items-center gap-1.5 rounded-md border border-primary-500 bg-primary-500 px-3 py-1.5 text-xs font-semibold text-white transition-colors hover:bg-primary-600"
            >
              <ExternalLink className="h-3.5 w-3.5" /> In Buchhaltung öffnen
            </button>
          ) : doc.booking_status === 'pending' ? (
            <button
              type="button"
              disabled={transferPending}
              onClick={handlers.onTransfer}
              className="inline-flex items-center gap-1.5 rounded-md border border-primary-500 bg-primary-500 px-3 py-1.5 text-xs font-semibold text-white transition-colors hover:bg-primary-600 disabled:opacity-60"
            >
              {transferPending
                ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                : <ExternalLink className="h-3.5 w-3.5" />}
              In Buchhaltung übernehmen
            </button>
          ) : null}
        </div>
      )}
    </Modal>
  )
}

function Meta({ label, value, mono }: { label: string; value: React.ReactNode; mono?: boolean }) {
  return (
    <div className="grid min-h-[30px] grid-cols-[104px_minmax(0,1fr)] items-center gap-2 border-t border-gray-100 px-3 py-1 first:border-t-0">
      <span className="text-[11px] text-gray-400">{label}</span>
      <span className={`min-w-0 break-words text-sm text-gray-900 ${mono ? 'font-mono text-xs' : ''}`}>{value}</span>
    </div>
  )
}
